import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Ellipsis, House, LayoutGrid, MessageSquare, Search, type LucideIcon } from "lucide-react";

import { useSidebarDrawer } from "@/components/SidebarDrawerContext";
import { useAuth } from "@/contexts/AuthContext";
import { useModuleVisibility } from "@/lib/moduleVisibility";
import { orgScopedPath } from "@/lib/orgDoor";
import { cn } from "@/lib/utils";

/**
 * The buyer's app-style tab bar below `lg` — Home, Deals, CRM, Messages, More.
 *
 * Mounted once by BuyerShell, so it sits on every buyer page. "More" opens the
 * sidebar drawer (the full navigation lives there; see SidebarDrawerContext),
 * so this bar only ever duplicates the four most-used destinations.
 *
 * Fixed to the viewport, so while it is mounted it flags <html data-bottom-nav>:
 * index.css then pads the body (the footer is not hidden under it) and lifts
 * the floating Niva launcher above it.
 */

type Tab = { key: string; label: string; icon: LucideIcon; to: string; module?: string; match: RegExp };

// Every target is a `/:orgSlug?/buyer/…` route, so each is org-prefixed. The
// match patterns allow that optional leading slug segment.
const TABS: Tab[] = [
  { key: "home", label: "Home", icon: House, to: "/buyer/dashboard", match: /^(\/[^/]+)?\/buyer\/dashboard/ },
  { key: "deals", label: "Deals", icon: Search, to: "/buyer/home", match: /^(\/[^/]+)?\/buyer\/home|^\/listings\// },
  { key: "crm", label: "CRM", icon: LayoutGrid, to: "/buyer/pipeline", module: "deal_crm", match: /^(\/[^/]+)?\/buyer\/pipeline/ },
  { key: "messages", label: "Messages", icon: MessageSquare, to: "/buyer/interests", module: "interests", match: /^(\/[^/]+)?\/buyer\/interests/ },
];

const ITEM = "flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium";

export default function BuyerBottomNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const drawer = useSidebarDrawer();
  const isVisible = useModuleVisibility();

  useEffect(() => {
    // Only flag the page while the bar is actually on screen (below lg), so
    // desktop never picks up the extra body padding.
    const mq = window.matchMedia("(max-width: 1023px)");
    const root = document.documentElement;
    const sync = () => (mq.matches ? root.setAttribute("data-bottom-nav", "") : root.removeAttribute("data-bottom-nav"));
    sync();
    mq.addEventListener("change", sync);
    return () => {
      mq.removeEventListener("change", sync);
      root.removeAttribute("data-bottom-nav");
    };
  }, []);

  return (
    <nav
      aria-label="Buyer"
      className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgb(0_0_0/0.06)] backdrop-blur lg:hidden"
    >
      {TABS.filter((t) => isVisible(t.module)).map(({ key, label, icon: Icon, to, match }) => {
        const active = match.test(pathname);
        // Colours on the children: the unlayered `a { color: inherit }` reset
        // beats Tailwind's text-* utilities on the anchor itself.
        return (
          <Link key={key} to={orgScopedPath(to, user?.org_slug)} aria-current={active ? "page" : undefined} className={ITEM}>
            <Icon className={cn("h-5 w-5", active ? "text-primary" : "text-muted-foreground")} />
            <span className={active ? "text-primary" : "text-muted-foreground"}>{label}</span>
          </Link>
        );
      })}
      {drawer && (
        <button type="button" onClick={drawer.toggle} className={cn(ITEM, "cursor-pointer")}>
          <Ellipsis className={cn("h-5 w-5", drawer.open ? "text-primary" : "text-muted-foreground")} />
          <span className={drawer.open ? "text-primary" : "text-muted-foreground"}>More</span>
        </button>
      )}
    </nav>
  );
}
